import { useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ROUTES, UserInfo, getUserInfo } from "./common";
import { AppContext } from "./context/app-context";
import { ChangePasswordPage } from "./pages/change-password";
import { FaceRegistrationPage } from "./pages/face-registration";
import { LoginPage } from "./pages/login";
import { NotFoundPage } from "./pages/not-found";
import { SecretQuestionsEnrollPage } from "./pages/secret-questions-enroll";
import { SecretQuestionsVerifyPage } from "./pages/secret-questions-verify";
import { VerifyMfaPage } from "./pages/verify-mfa";
import { SalesforceParamsKeeper } from "./parts/salesforce-params-keeper";
import {
  AuthProvider,
  RequireAuth,
  RequireFaceRegistered,
  RequireSecretQuestionsRegistered,
} from "./provider";

import "./App.css";

export default function App() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(getUserInfo());

  return (
    <AuthProvider>
      <AppContext.Provider value={{ userInfo, setUserInfo }}>
        <SalesforceParamsKeeper />
        <Routes>
          <Route path="/" element={<Navigate to={ROUTES.checkin} replace />} />

          <Route path={ROUTES.login} element={<LoginPage />} />
          <Route path={ROUTES.changePassword} element={<ChangePasswordPage />} />

          <Route
            path={ROUTES.faceRegistration}
            element={
              <RequireAuth>
                <FaceRegistrationPage />
              </RequireAuth>
            }
          />

          {/* Secret-question enrollment. Runs only after face registration, so it is gated by
              RequireFaceRegistered — an unregistered face is redirected to /face-registration
              first, and the gate on /verify-mfa then routes here to enroll. */}
          <Route
            path={ROUTES.secretQuestions}
            element={
              <RequireAuth>
                <RequireFaceRegistered>
                  <SecretQuestionsEnrollPage />
                </RequireFaceRegistered>
              </RequireAuth>
            }
          />

          {/* FE-2: answer a secret question when face MFA can't be used (camera error).
              Reached from /verify-mfa after enable-fallback returns 200. */}
          <Route
            path={ROUTES.secretQuestionsVerify}
            element={
              <RequireAuth>
                <RequireFaceRegistered>
                  <SecretQuestionsVerifyPage />
                </RequireFaceRegistered>
              </RequireAuth>
            }
          />

          <Route
            path={ROUTES.checkin}
            element={
              <RequireAuth>
                <RequireFaceRegistered>
                  <RequireSecretQuestionsRegistered>
                    <VerifyMfaPage />
                  </RequireSecretQuestionsRegistered>
                </RequireFaceRegistered>
              </RequireAuth>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppContext.Provider>
    </AuthProvider>
  );
}
